/**
 * Sorts an array of objects in reverse alphabetical order by the `name` property.
 * 
 * @param {Object[]} array - The array to sort.
 * @returns {Object[]} - The sorted array.
 */
export function sortArrayByNameReverse(array) {
    return array.sort((a, b) => b.name?.toLowerCase().localeCompare(a.name?.toLowerCase()));
}


/**
 * Sorts an array of objects alphabetically by the `name` property.
 * 
 * @param {Object[]} array - The array to sort.
 * @returns {Object[]} - The sorted array.
 */
export function sortArrayByName(array) {
    return array.sort((a, b) => a.name?.toLowerCase().localeCompare(b.name?.toLowerCase()));
}

/**
 * Checks if an array of objects is sorted alphabetically by the `name` property.
 * 
 * @param {Object[]} arr - The array to check.
 * @returns {number} - Returns `1` if sorted in ascending order, `0` if in descending order, `-1` if unsorted.
 */
export function checkAlphabeticalOrder(arr) {
    return JSON.stringify(arr) === JSON.stringify(sortArrayByName([...arr])) ? 1 :
           JSON.stringify(arr) === JSON.stringify(sortArrayByNameReverse([...arr])) ? 0 : -1;
}


/**
 * Rearranges an object in the array from its current position to a new position.
 *
 * @param {Array} array - The original array of objects.
 * @param {number} fromIndex - The current index of the object to move.
 * @param {number} toIndex - The new index where the object should be placed.
 * @returns {Array} - A new array with the object rearranged.
 *
 * @example
 * const arr = [{ id: 1 }, { id: 2 }, { id: 3 }];
 * const result = rearrangeArray(arr, 0, 2);
 * // Result: [{ id: 2 }, { id: 3 }, { id: 1 }]
 */
export function rearrangeArray(array, fromIndex, toIndex) {
    // Validate input
    if (
        !Array.isArray(array) ||
        fromIndex < 0 || fromIndex >= array.length ||
        toIndex < 0 || toIndex >= array.length
    ) {
        throw new Error('Invalid indices or input array');
    }

    // Create a copy of the array
    const newArray = [...array];

    // Remove the element from the current position
    const [item] = newArray.splice(fromIndex, 1);

    // Insert the element at the new position
    newArray.splice(toIndex, 0, item);

    return newArray;
}


/**
 * Splits an array into chunks of a given size.
 * 
 * @param {Array} arr - The array to split.
 * @param {number} size - The size of each chunk.
 * @returns {Array[]} - An array of chunked arrays.
 */
export function chunkArrayInGroups(arr, size) {
    return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, (i + 1) * size));
}


/**
 * Finds the first key in an object that matches a given value.
 * 
 * @param {Object} object - The object to search through.
 * @param {*} value - The value to find the corresponding key for.
 * @returns {string|undefined} - The key that matches the value, or undefined if not found.
 */
export function getKeyByValue(object, value) {
    return Object.keys(object).find((key) => object[key] === value);
}


export function includes(arr, key, value) {
    let found = false;
    arr?.forEach((item) => {
        if (
            item[key] &&
            value &&
            typeof value === "string" &&
            item[key].toLowerCase() === value.toLowerCase()
        ) {
            found = item;
        }
    });
    return found;
}


/**
 * Converts bytes into a human-readable format.
 * 
 * @param {number} bytes - The number of bytes.
 * @param {number} [decimals=2] - The number of decimal places.
 * @returns {string} - The formatted size string.
 */
export function formatBytes(bytes, decimals = 2) {
    if (!bytes) return "0 Bytes";
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / 1024 ** i).toFixed(decimals < 0 ? 0 : decimals)} ${sizes[i]}`;
}

/**
 * Deep clones an object.
 * 
 * @param {Object} obj - The object to clone.
 * @returns {Object} - The cloned object.
 */
export function cloneObject(obj) {
    return structuredClone(obj);
}
